import { useEffect, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Copy, Globe, RefreshCw, ShieldAlert, Wifi } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card, fmtNum, fmtWhen } from "./ui";

// Integrity routes are staff-only and not part of the public contract, so they are named here rather than in ApiRoutes.
const BASE = "/api/admin/integrity";

const SEV = { high: "border-red-500/60 bg-red-500/10 text-red-200", medium: "border-amber-400/50 bg-amber-400/10 text-amber-100", low: "border-slate-500/40 bg-white/5 text-slate-300" };
const KIND_LABEL = {
  SAME_IP: "Same internet address", SAME_SUBNET: "Same local network", SAME_ISP: "Same ISP and country",
  SAME_PLACE: "Same place (devices metres apart)",
  CHIP_FLOW: "Chips flowing one way", SOFT_PLAY: "Soft play", CO_SEATING: "Always at the same table",
  RAISE_SQUEEZE: "Raising each other out",
  ORACLE_FOLDS: "Folded the winning hand", WIN_RATE: "Win rate outlier", TIMING: "Machine-like timing",
};

const MODES = [
  ["block", "Block everywhere", "A second account on the same connection, or in the same room, cannot sit at a table someone is already playing at. Watching is always allowed"],
  ["block_cash", "Block cash only", "Blocked on cash tables, flagged on Sit & Go and tournaments"],
  ["flag", "Flag only", "Nothing is blocked — matches are recorded for review"],
];

const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied"); };

const Incidents = ({ rows, showWith }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-[11px]" data-testid="integrity-incidents">
      <thead className="text-slate-500 uppercase tracking-[0.15em]">
        <tr>
          <th className="text-left py-1 pr-3">When</th>
          <th className="text-left py-1 pr-3">Hand</th>
          <th className="text-left py-1 pr-3">Table</th>
          {showWith && <th className="text-left py-1 pr-3">With</th>}
          <th className="text-left py-1 pr-3">Rounds</th>
          <th className="text-left py-1 pr-3">Pushed out</th>
          <th className="text-left py-1">Pot</th>
        </tr>
      </thead>
      <tbody className="text-slate-300">
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-white/5" data-testid={`integrity-incident-${i}`}>
            <td className="py-1 pr-3 whitespace-nowrap">{fmtWhen(r.at)}</td>
            <td className="py-1 pr-3 whitespace-nowrap">
              <button onClick={() => copy(r.hand_id || "")} className="font-mono hover:text-amber-200 flex items-center gap-1"
                title="Copy the hand id for the replay lookup">#{r.hand_no} <Copy className="w-3 h-3 opacity-50" /></button>
            </td>
            <td className="py-1 pr-3">{r.table_id}</td>
            {showWith && <td className="py-1 pr-3 text-amber-100">{r.with}</td>}
            <td className="py-1 pr-3">{(r.streets || []).join(", ")}</td>
            <td className="py-1 pr-3">{r.folds}</td>
            <td className="py-1">{r.pot_to ? "to one of them" : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Flag = ({ f }) => {
  const [open, setOpen] = useState(false);
  const inc = f.incidents || [];
  return (
    <div className={`rounded-xl border p-3 flex flex-col gap-1 ${SEV[f.severity]}`} data-testid={`integrity-flag-${f.kind}`}>
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
        <span className="text-xs uppercase tracking-[0.2em]">{KIND_LABEL[f.kind] || f.kind}</span>
        <span className="text-[10px] opacity-70">· {f.severity}</span>
        {f.new && <span className="text-[10px] rounded-full bg-red-500/80 text-white px-2 py-0.5" data-testid="integrity-flag-new">NEW</span>}
      </div>
      <div className="text-sm text-amber-50">{(f.names || f.pair).join(" + ")}</div>
      <div className="text-xs opacity-90">{f.detail}</div>
      {inc.length > 0 && (
        <button onClick={() => setOpen(!open)} data-testid={`integrity-flag-hands-${f.kind}`}
          className="self-start text-[11px] text-amber-200 hover:text-amber-100 flex items-center gap-1 mt-1">
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} {inc.length} hand(s)
        </button>
      )}
      {open && <Incidents rows={inc} />}
      <pre className="text-[10px] font-mono opacity-60 whitespace-pre-wrap break-all">{JSON.stringify(f.evidence)}</pre>
    </div>
  );
};

// One block per player, with every partner and every hand merged into one timeline: the common element is obvious.
const WatchRow = ({ r, first }) => {
  const [open, setOpen] = useState(first);
  return (
    <div className="rounded-xl border border-white/10 bg-black/20" data-testid={`integrity-watch-${r.user_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full p-3 flex items-center gap-3 text-left">
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        <span className="text-sm text-amber-50">{r.name}</span>
        <span className="text-[11px] text-slate-400">
          {r.partners.length} player(s) · {r.incidents.length} hand(s) · {r.tables.length} table(s)
        </span>
        <span className="ml-auto flex items-center gap-1">
          {r.high > 0 && <span className="text-[10px] rounded-full border border-red-500/60 bg-red-500/10 text-red-200 px-2 py-0.5">{r.high} serious</span>}
          {r.medium > 0 && <span className="text-[10px] rounded-full border border-amber-400/50 bg-amber-400/10 text-amber-100 px-2 py-0.5">{r.medium} medium</span>}
          {r.low > 0 && <span className="text-[10px] rounded-full border border-white/15 text-slate-300 px-2 py-0.5">{r.low} low</span>}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 flex flex-col gap-2">
          <div className="flex flex-wrap gap-1">
            {r.partners.map((p) => (
              <span key={p.user_id} className="text-[11px] rounded-full border border-white/15 px-2 py-0.5 text-slate-200">
                {p.name} <span className="opacity-60">· {p.kinds.map((k) => KIND_LABEL[k] || k).join(", ")}</span>
              </span>
            ))}
          </div>
          {r.incidents.length > 0 ? <Incidents rows={r.incidents} showWith />
            : <div className="text-[11px] text-slate-500">No individual hands for these signals — see the flag list below.</div>}
        </div>
      )}
    </div>
  );
};

// Staff view of the collusion / superuser watch: the seating rules, the watch list, and the last automatic scan.
export const IntegrityPanel = () => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [radius, setRadius] = useState(null);
  const [sq, setSq] = useState(null); // {squeeze_min, squeeze_rate_pct}
  const load = () => api.get(BASE).then((r) => {
    setState(r.data);
    setRadius((cur) => (cur === null ? (r.data.proximity_metres ?? 10) : cur));
    setSq((cur) => cur || { min: r.data.limits?.squeeze_min ?? 4, pct: Math.round((r.data.limits?.squeeze_rate ?? 0.1) * 100) });
  }).catch(() => toast.error("Could not load the integrity data"));
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, []);
  const scan = () => {
    setBusy(true);
    api.post(`${BASE}/scan?days=30`).then((r) => { toast.success(`Scanned ${fmtNum(r.data.hands)} hands — ${r.data.flags.length} flag(s)`); load(); })
      .catch(() => toast.error("Scan failed")).finally(() => setBusy(false));
  };
  const setMode = (m) => api.patch(`${BASE}/mode`, { same_network: m }).then(() => { toast.success("Seating rule saved"); load(); });
  const saveRadius = (v) => api.patch(`${BASE}/proximity`, { proximity_metres: Number(v) })
    .then(() => { toast.success(Number(v) > 0 ? `Distance limiter set to ${v} m` : "Distance limiter turned off"); load(); })
    .catch(() => toast.error("Could not save the distance limiter"));
  const saveSqueeze = () => api.patch(`${BASE}/limits`, { squeeze_min: Number(sq.min), squeeze_rate: Number(sq.pct) / 100 })
    .then(() => { toast.success("Sensitivity saved — it applies to the next scan"); load(); })
    .catch(() => toast.error("Could not save the sensitivity"));
  const saveRegion = (patch) => api.patch("/api/admin/region", patch)
    .then(() => { toast.success("Region rule saved"); load(); })
    .catch(() => toast.error("Could not save the region rule"));
  if (!state) return <div className="text-sm text-slate-400" data-testid="integrity-loading">Loading…</div>;
  const flags = state.run?.flags || [];
  const watch = state.run?.watch || [];
  return (
    <div className="flex flex-col gap-6" data-testid="integrity-panel">
      <Card title="Watch list — every signal, by player" testId="integrity-watch-card">
        <p className="text-[11px] text-slate-400 mb-3">
          One block per player, so if the same account keeps turning up — with different partners, or with the same
          partner at different tables — it sits at the top of this list with every hand number and time in one place.
          Click a hand number to copy its id for the replay lookup. Nothing here is a punishment: it is a list to watch.
        </p>
        {watch.length === 0
          ? <div className="text-sm text-slate-400" data-testid="integrity-watch-empty">Nobody on the watch list.</div>
          : <div className="flex flex-col gap-2">{watch.map((r, i) => <WatchRow key={r.user_id} r={r} first={i === 0} />)}</div>}
      </Card>

      <Card title="Where players can play from" testId="integrity-region-card">
        <p className="text-[11px] text-slate-400 mb-3">
          The country, the ISP and whether the address belongs to a VPN or a data centre are read on every sign-up and
          sign-in. Players Room is open worldwide — no country is turned away. With the VPN switch on (and turn-aways
          applied), anyone hiding behind a VPN sees a screen telling them to turn it off and try again.
        </p>
        <div className="flex flex-col gap-2">
          <button data-testid="region-mode-enforce" onClick={() => saveRegion({ mode: "enforce" })}
            className={`rounded-xl border p-3 text-left ${state.region?.mode === "enforce" ? "border-amber-400 bg-amber-500/10" : "border-white/10 hover:border-white/30"}`}>
            <span className="text-sm text-amber-50 block">Apply turn-aways</span>
            <span className="text-[11px] text-slate-400">The VPN switch below is enforced at sign-up, guest play and sign-in</span>
          </button>
          <button data-testid="region-mode-log" onClick={() => saveRegion({ mode: "log" })}
            className={`rounded-xl border p-3 text-left ${state.region?.mode === "log" ? "border-amber-400 bg-amber-500/10" : "border-white/10 hover:border-white/30"}`}>
            <span className="text-sm text-amber-50 block">Note only</span>
            <span className="text-[11px] text-slate-400">Nobody is turned away — VPN sign-ins are just recorded and flagged</span>
          </button>
          <button data-testid="region-block-proxy" onClick={() => saveRegion({ block_proxy: !state.region?.block_proxy })}
            className={`rounded-xl border p-3 text-left flex items-center gap-3 ${state.region?.block_proxy ? "border-amber-400 bg-amber-500/10" : "border-white/10 hover:border-white/30"}`}>
            <Globe className={`w-4 h-4 ${state.region?.block_proxy ? "text-amber-300" : "text-slate-500"}`} />
            <span>
              <span className="text-sm text-amber-50 block">Turn away VPNs, proxies and data-centre addresses {state.region?.block_proxy ? "· ON" : "· OFF"}</span>
              <span className="text-[11px] text-slate-400">Only has an effect while turn-aways are applied</span>
            </span>
          </button>
        </div>
        {(state.region_blocks || []).length > 0 && (
          <div className="mt-4" data-testid="region-blocks">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Recently turned away</div>
            <div className="flex flex-col gap-1">
              {state.region_blocks.map((b, i) => (
                <div key={i} className="text-[11px] text-slate-300 flex flex-wrap gap-2" data-testid={`region-block-${i}`}>
                  <span className="text-slate-500">{fmtWhen(b.at)}</span>
                  <span>{b.stage}</span>
                  <span className="text-amber-100">{b.country || "unknown country"}</span>
                  <span className="text-slate-500">{b.isp || b.ip}</span>
                  {(b.proxy || b.hosting) && <span className="text-red-300">VPN / data centre</span>}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">If a genuine player shows up here, turn the VPN switch off and tell me.</p>
          </div>
        )}
        {(state.age_refusals || []).length > 0 && (
          <div className="mt-4" data-testid="age-refusals">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Under-18 sign-ups turned away</div>
            <div className="flex flex-col gap-1">
              {state.age_refusals.map((a, i) => (
                <div key={i} className="text-[11px] text-slate-300 flex flex-wrap gap-2" data-testid={`age-refusal-${i}`}>
                  <span className="text-slate-500">{fmtWhen(a.at)}</span>
                  <span className="text-amber-100">{a.email || "no email"}</span>
                  <span>{a.dob || "—"}{a.age != null ? ` · age ${a.age}` : ""}</span>
                  <span className="text-slate-500">{a.ip}</span>
                  <span className="text-red-300">{a.reason === "UNDER_18" ? "under 18" : a.reason === "COOLDOWN" ? "tried again too soon" : "bad date"}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Date of birth is required at sign-up and checked on the server; a browser that was just refused is held off for 2 hours so nobody can simply try another birthday.</p>
          </div>
        )}
      </Card>

      <Card title="Same connection at one table" testId="integrity-mode-card">
        <div className="flex flex-col gap-2">
          {MODES.map(([id, label, hint]) => (
            <button key={id} data-testid={`integrity-mode-${id}`} onClick={() => setMode(id)}
              className={`rounded-xl border p-3 text-left flex items-start gap-3 ${state.mode === id ? "border-amber-400 bg-amber-500/10" : "border-white/10 hover:border-white/30"}`}>
              <Wifi className={`w-4 h-4 mt-0.5 ${state.mode === id ? "text-amber-300" : "text-slate-500"}`} />
              <span><span className="text-sm text-amber-50 block">{label}</span><span className="text-[11px] text-slate-400">{hint}</span></span>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-3">Each account's address, local network and ISP come from its own sign-in record. Bots are never counted.</p>
      </Card>

      <Card title="Distance limiter" testId="integrity-proximity-card">
        <p className="text-[11px] text-slate-400 mb-3">
          Two players whose devices are this close together are treated as sitting in the same room, even on different
          connections — the second one is kept out of the table (watching is still allowed). Only precise device
          readings count; an address-derived position is city-wide and is never used, so honest players in the same city
          are never caught. Browsers only report a position when the player has allowed it; the phone apps will report
          it for everyone.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <input data-testid="integrity-proximity-input" type="number" min="0" max="5000" step="1" value={radius ?? ""}
            onChange={(e) => setRadius(e.target.value)}
            className="w-24 rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-sm text-amber-50" />
          <span className="text-xs text-slate-400">metres · 0 turns it off</span>
          <button data-testid="integrity-proximity-save" onClick={() => saveRadius(radius)}
            className="btn-gold rounded-full px-4 py-2 text-xs font-display tracking-wider">Save</button>
          <span className="text-xs text-slate-500" data-testid="integrity-proximity-devices">
            {fmtNum(state.devices_sharing_location || 0)} account(s) currently share a device position
          </span>
        </div>
      </Card>

      <Card title="Raising each other out — sensitivity" testId="integrity-squeeze-card">
        <p className="text-[11px] text-slate-400 mb-3">
          A "squeeze" is one betting round where two players both raised and someone else folded while facing it. It
          happens honestly all the time; what matters is the same two accounts doing it again and again. A pair is
          flagged when it happens at least this many times AND in at least this share of the hands they played together.
          Lower numbers = more warnings, more of them harmless. Changes apply to the next scan.
        </p>
        {sq && (
          <div className="flex items-center gap-2 flex-wrap">
            <input data-testid="integrity-squeeze-min" type="number" min="2" max="100" value={sq.min}
              onChange={(e) => setSq({ ...sq, min: e.target.value })}
              className="w-20 rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-sm text-amber-50" />
            <span className="text-xs text-slate-400">times, and at least</span>
            <input data-testid="integrity-squeeze-rate" type="number" min="1" max="100" value={sq.pct}
              onChange={(e) => setSq({ ...sq, pct: e.target.value })}
              className="w-20 rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-sm text-amber-50" />
            <span className="text-xs text-slate-400">% of their shared hands</span>
            <button data-testid="integrity-squeeze-save" onClick={saveSqueeze}
              className="btn-gold rounded-full px-4 py-2 text-xs font-display tracking-wider">Save</button>
            <span className="text-[11px] text-slate-500">Middle setting = 4 times and 10%</span>
          </div>
        )}
      </Card>

      <Card title="Hand-history scan" testId="integrity-scan-card">
        <div className="flex items-center gap-3 flex-wrap">
          <button data-testid="integrity-scan-button" onClick={scan} disabled={busy}
            className="btn-gold rounded-full px-4 py-2 text-xs font-display tracking-wider flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Run scan now
          </button>
          <span className="text-xs text-slate-400" data-testid="integrity-last-run">
            {state.run ? `Last run ${fmtWhen(state.run.at)} · ${fmtNum(state.run.hands)} hands · ${state.run.flags.length} flag(s)` : "No scan yet — it runs automatically every 20 minutes"}
            {state.run?.new_count ? ` · ${state.run.new_count} new` : ""}
          </span>
        </div>
        <div className="mt-4 flex flex-col gap-2" data-testid="integrity-flags">
          {flags.length === 0 && <div className="text-sm text-slate-400" data-testid="integrity-no-flags">Nothing flagged. Chip flow, soft play, raising each other out, shared tables, oracle folds, win rates and decision timing all look normal.</div>}
          {flags.map((f, i) => <Flag key={i} f={f} />)}
        </div>
      </Card>

      <Card title="Seating matches (live)" testId="integrity-seat-card">
        {(state.seat_flags || []).length === 0
          ? <div className="text-sm text-slate-400" data-testid="integrity-no-seat-flags">No two accounts have tried to share a table from one connection.</div>
          : <div className="flex flex-col gap-2">
            {state.seat_flags.map((f, i) => (
              <div key={i} className={`rounded-xl border p-3 flex items-center gap-3 ${SEV[f.severity]}`} data-testid={`integrity-seat-flag-${i}`}>
                <ShieldAlert className="w-4 h-4 shrink-0" />
                <div className="flex-1 text-xs">
                  <div className="text-sm text-amber-50">{f.pair.join(" + ")}</div>
                  <div className="opacity-90">{KIND_LABEL[f.kind] || f.kind} · table {f.table_id} · {fmtWhen(f.at)} · {f.blocked ? "blocked from sitting" : "allowed, flagged"}</div>
                </div>
              </div>
            ))}
          </div>}
      </Card>
    </div>
  );
};
